import {DeepReadonly} from 'ts-essentials'
import {logger, LongId, Util} from 'yougile-util'
import {
  DataObjectId,
  Hub,
  nothingAllowedBoardPermissions,
  nothingAllowedColumnPermissions,
  PermissionObject,
  PermissionsById,
  SandboxTree,
  VisibilityOptions
} from 'yougile-model'
import {Debouncer} from '../../common/debouncer'
import {ChatUtils} from '../extensions/chat-utils'
import {globals} from '../extensions/globals-base'
import {Sticker} from '../extensions/sticker/sticker'
import {Task} from '../extensions/task'
import {Connector} from './connector'
import {PermissionsHelper} from './permissions-helper'
import {Sync} from './sync'
import {SyncEvents} from './sync-events'

/**
 * Synchronizes permissions with server and can calculate what is
 * available for the current user and what is not, based on current user’s
 * permissions.
 */

export class PermissionsClient {
  private static listeners: Map<LongId, () => void> = new Map()
  private static updateDebouncer = new Debouncer(500)
  private static initializedAlready = false

  public static addListener(l: () => void): LongId {
    const id = Util.longId()
    PermissionsClient.listeners.set(id, l)
    return id
  }

  public static init(): void {
    if (this.initializedAlready) {
      return
    }
    this.initializedAlready = true

    PermissionsClient.addListener(() => {
      Sync.listSandboxes().forEach(b => SyncEvents.triggerAll(b.companyId))
      const currentCompany = globals.getCompanyId()
      if (currentCompany) {
        globals.setCompanyId(currentCompany)
      }
    })
  }

  public static onConnected(): void {
    Connector.onRequest('permissions-up', req => {
      if (req.companyId) {
        PermissionsClient.trigger()
      }
    })
  }

  public static removeListener(id: LongId): void {
    PermissionsClient.listeners.delete(id)
  }

  public static trigger(): void {
    PermissionsClient.updateDebouncer.runButNotOften(() => {
      ;[...PermissionsClient.listeners.values()].forEach(l =>
        Util.catch(() => l())
      )
    })
  }
}

export class PermissionsClientHelper {
  public static getAllChildObjects(
    companyId: DataObjectId,
    parentId: DataObjectId,
    userId: DataObjectId
  ): PermissionObject[] {
    const res: PermissionObject[] = []
    const box = Sync.get({companyId})
    if (!box) {
      return []
    }

    const permissions = PermissionsHelper.getAllCustomRolesPermissions(
      userId,
      Sync.get({companyId})
    )

    Object.keys(permissions)
      .map((id: DataObjectId) => permissions[id])
      .forEach(p => {
        if (box.isParentOf(parentId, p.objectId)) {
          res.push(p)
        }
      })
    return res
  }

  /**
   * Get permission object with correct type, based on the given
   * permission object. E.g.: suppose we have permission object for board and
   * we have some task inside this board that is assigned to a current user.
   * Then this function will return:
   * {
   *   type: 'task',
   *   id: <task id>,
   *   permissions: permissions.boards.columns.myTasks
   * }
   */
  private static extractPermissions(
    companyId: DataObjectId,
    objectId: DataObjectId,
    pObj: PermissionObject,
    userId: DataObjectId,
    opts?: VisibilityOptions
  ): DeepReadonly<PermissionObject> | undefined {
    if (!pObj) {
      return undefined
    }
    const box = Sync.get({companyId})
    if (!box) {
      return undefined
    }
    const obj = box.get(objectId) as Hub
    if (!obj) {
      return undefined
    }
    const type = box.getType(obj)
    if (type === pObj.type) {
      return pObj.objectId === objectId ? pObj : undefined
    }
    if (type === 'board') {
      if (!pObj?.permissions) {
        logger.log('No permissions')
      }
      return pObj.type === 'project' && pObj.objectId === obj.location
        ? {
            type: 'board',
            objectId,
            permissions:
              pObj.permissions?.boards ?? nothingAllowedBoardPermissions()
          }
        : undefined
    } else if (type === 'column') {
      if (pObj.type === 'board') {
        return pObj.objectId === obj.location
          ? {
              type: 'column',
              objectId,
              permissions:
                pObj.permissions?.columns ?? nothingAllowedColumnPermissions()
            }
          : undefined
      } else if (pObj.type === 'project') {
        const boardPermissions = PermissionsClientHelper.extractPermissions(
          companyId,
          obj.location,
          pObj,
          userId,
          opts
        )
        return PermissionsClientHelper.extractPermissions(
          companyId,
          objectId,
          boardPermissions,
          userId,
          opts
        )
      } else {
        return undefined
      }
    } else if (type === 'task') {
      if (pObj.type === 'column') {
        if (pObj.objectId === obj.location) {
          const colPerm = pObj.permissions
          const isMy = () => {
            return (
              (opts && opts.considerAsMy) ||
              Sticker.getUsers(obj).includes(userId || globals.currentUserId)
            )
          }
          const isCreatedByMe = () => {
            const creator = Task.creator(obj)
            return creator && creator.id === (userId || globals.currentUserId)
          }
          const isWithMe = () => {
            return (
              (opts && opts.considerAsWithMe) ||
              ChatUtils.isMyChat(box, objectId, userId)
            )
          }
          return {
            type: 'task',
            objectId,
            permissions:
              colPerm.createdByMeTasks && isCreatedByMe()
                ? colPerm.createdByMeTasks
                : colPerm.myTasks && isMy()
                ? colPerm.myTasks
                : colPerm.withMeTasks && isWithMe()
                ? colPerm.withMeTasks
                : colPerm.allTasks
          }
        } else {
          return undefined
        }
      } else {
        const parentPerm = PermissionsClientHelper.extractPermissions(
          companyId,
          obj.location,
          pObj,
          userId,
          opts
        )
        return PermissionsClientHelper.extractPermissions(
          companyId,
          objectId,
          parentPerm,
          userId,
          opts
        )
      }
    } else {
      return undefined
    }
  }

  private static permissionsForObject(
    box: SandboxTree,
    objectId: DataObjectId,
    permissions: PermissionsById,
    userId: DataObjectId,
    opts?: VisibilityOptions
  ): PermissionObject | undefined {
    if (!box || !permissions) {
      return undefined
    }
    const obj = box.get(objectId)
    if (!obj) {
      return undefined
    }
    const parents = box.getAllParentHubs(obj)
    const ids = [obj, ...parents].map(o => o.id)
    for (const id of ids) {
      if (permissions[id]) {
        return PermissionsClientHelper.extractPermissions(
          box.companyId,
          objectId,
          permissions[id],
          userId,
          opts
        )
      }
    }
    return undefined
  }

  public static getPermissions(
    companyId: DataObjectId,
    objectId: DataObjectId,
    userId: DataObjectId,
    opts?: VisibilityOptions
  ): PermissionObject | undefined {
    const box = Sync.get({companyId})
    if (!box) {
      return undefined
    }
    const obj = box.get(objectId)
    if (!obj) {
      return undefined
    }
    const rolePerm = PermissionsHelper.getRolePermissions(userId, box, objectId)
    const permissions = PermissionsClientHelper.permissionsForObject(
      box,
      objectId,
      rolePerm,
      userId,
      opts
    )?.permissions
    const type = box.getType(obj)
    return {
      type,
      objectId,
      permissions
    } as PermissionObject
  }
}
